import config from 'config';
import { AgentScannerService } from '@mulesoft/agent-scanner-client';

/**
 * Helper to determine if code is running on the client (browser)
 */
const isClient = () => typeof window !== 'undefined';

/**
 * Client-side service instance (browser)
 * Uses relative URLs (/agent-scanner/...) that go through the Express proxy
 * The proxy then forwards to config.scannerAPI
 */
const clientService = new AgentScannerService({
  api: {
    uri: {
      pathname: '' // Empty -> relative URLs like /agent-scanner/api/v1/...
    }
  },
  scannerAPI: config.scannerAPI,
  skipProxyPrefix: false // Browser: add /agent-scanner prefix (proxy intercepts)
});

/**
 * Server-side service instance (SSR)
 * Uses absolute URLs directly to the backend (config.scannerAPI)
 * skipProxyPrefix=true means URLs are /api/v1/... (no /agent-scanner prefix)
 */
const serverService = new AgentScannerService({
  api: config.scannerAPI, // Full backend URL (e.g., http://localhost:8080 or https://stgx.../agent-scanner)
  scannerAPI: config.scannerAPI,
  skipProxyPrefix: true // SSR: don't add /agent-scanner prefix, call backend directly
});

/**
 * Get the appropriate service instance based on execution context
 * @returns {AgentScannerService} Client or server service instance
 */
const getService = () => (isClient() ? clientService : serverService);

/**
 * List all agent scanners for an organization
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {Object} params.context - Request context with authorization
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.size - Page size (default: 10000)
 * @returns {Promise<Object>} Response with paginated scanners data
 */
export function list({ organizationId, context, page = 0, size = 10000 }) {
  if (!organizationId) {
    throw new Error('organizationId is required');
  }

  return getService().list({ organizationId, context, page, size });
}

/**
 * Create a new agent scanner
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {Object} params.context - Request context with authorization
 * @param {Object} params.data - Scanner configuration data
 * @returns {Promise<Object>} Response with created scanner data
 */
export function create({ organizationId, context, data }) {
  return getService().create({ organizationId, data, context });
}

/**
 * Update an existing agent scanner
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {string} params.scannerId - Scanner ID to update
 * @param {Object} params.context - Request context with authorization
 * @param {Object} params.data - Scanner configuration data
 * @returns {Promise<Object>} Response with updated scanner data
 */
export function update({ organizationId, scannerId, context, data }) {
  return getService().update({ organizationId, scannerId, data, context });
}

/**
 * Delete an agent scanner
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {string} params.scannerId - Scanner ID to delete
 * @param {Object} params.context - Request context with authorization
 * @param {boolean} params.deleteAgents - If true, also deletes associated agents (default: false)
 * @returns {Promise<void>} Promise that resolves when deletion is complete
 */
export function deleteScanner({
  organizationId,
  scannerId,
  context,
  deleteAgents = false
}) {
  return getService().delete({
    organizationId,
    scannerId,
    context,
    deleteAgents
  });
}

/**
 * Execute a scanner configuration workflow
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {string} params.scannerId - Scanner ID to execute
 * @param {Object} params.context - Request context with authorization
 * @returns {Promise<void>} Promise that resolves when execution is triggered
 */
export function execute({ organizationId, scannerId, context }) {
  return getService().execute({ organizationId, scannerId, context });
}

/**
 * List scanner runs (history) for a specific scanner
 * @param {Object} params - Parameters for the request
 * @param {string} params.scannerId - Scanner ID
 * @param {string} params.organizationId - Organization ID
 * @param {Object} params.context - Request context with authorization
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.size - Page size (default: 20)
 * @returns {Promise<Object>} Response with paginated scanner runs data
 */
export function listRuns({
  scannerId,
  organizationId,
  context,
  page = 0,
  size = 20
}) {
  return getService().listRuns({
    scannerId,
    organizationId,
    context,
    page,
    size
  });
}

/**
 * Get available target systems for connections
 * @param {Object} params - Parameters for the request
 * @param {Object} params.context - Request context with authorization
 * @returns {Promise<Array>} Response with list of target systems
 */
export function getTargetSystems({ context }) {
  return getService().getTargetSystems({ context });
}

/**
 * Test connection to a target system
 * @param {Object} params - Parameters for the request
 * @param {string} params.targetSystemType - Target system type (e.g., 'bedrock', 'openai')
 * @param {Object} params.connectionParams - Connection parameters (includes id field)
 * @param {Object} params.context - Request context with authorization
 * @returns {Promise<boolean>} True if connection successful, false otherwise
 */
export function testConnection({
  targetSystemType,
  connectionParams,
  context
}) {
  return getService().testConnection({
    targetSystemType,
    connectionParams,
    context
  });
}

/**
 * List Stagin Agents for a specific scanner run
 * @param {Object} params - Parameters for the request
 * @param {string} params.scanRunId - Scanner Run ID
 * @param {string} params.scannerId - Scanner ID
 * @param {string} params.organizationId - Organization ID
 * @param {Object} params.context - Request context with authorization
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.size - Page size (default: 20)
 * @returns {Promise<Object>} Response with paginated scanner runs data
 */
export function getStagingAgents({
  organizationId,
  scannerId,
  scanRunId,
  context,
  page = 0,
  size = 20
}) {
  return getService().getStagingAgents({
    organizationId,
    scannerId,
    scanRunId,
    context,
    page,
    size
  });
}
